// frontend/src/hooks/useEntropyOracle.js
import { useState, useEffect, useCallback } from "react";
import web3Service from "../services/web3Service";

/**
 * Hook React pour interagir avec le contrat EntropyOracle
 */
export function useEntropyOracle() {
  const [contract, setContract] = useState(null);
  const [address, setAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialise le contrat
  useEffect(() => {
    initContract();
  }, []);

  const initContract = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const entropyOracle = await web3Service.getContract("EntropyOracle");
      setContract(entropyOracle);
      
      const addr = await entropyOracle.getAddress();
      setAddress(addr);

      console.log("✅ EntropyOracle initialisé à", addr);
    } catch (err) {
      console.error("❌ Erreur d'initialisation EntropyOracle:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Récupère l'entropie actuelle
  const getCurrentEntropy = useCallback(async () => {
    if (!contract) return null;

    try {
      const entropy = await contract.getCurrentEntropy();
      return {
        value: entropy[0].toString(),
        timestamp: Number(entropy[1]),
        source: entropy[2]
      };
    } catch (err) {
      console.error("❌ Erreur getCurrentEntropy:", err);
      throw err;
    }
  }, [contract]);

  // Récupère l'historique de l'entropie
  const getEntropyHistory = useCallback(async (count = 10) => {
    if (!contract) return [];

    try {
      const history = await contract.getEntropyHistory(count);
      return history.map(item => ({
        value: item[0].toString(),
        timestamp: Number(item[1]),
        source: item[2]
      }));
    } catch (err) {
      console.error("❌ Erreur getEntropyHistory:", err);
      throw err;
    }
  }, [contract]);

  // Commit une nouvelle valeur d'entropie
  const commitEntropy = useCallback(async (entropyValue, source = "frontend") => {
    if (!contract) throw new Error("Contrat non initialisé");

    try {
      const tx = await contract.commitEntropy(entropyValue, source);
      console.log("📤 Transaction envoyée:", tx.hash);
      
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmée:", receipt.transactionHash);
      
      return receipt;
    } catch (err) {
      console.error("❌ Erreur commitEntropy:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le nombre total de commits
  const getTotalCommits = useCallback(async () => {
    if (!contract) return 0;

    try {
      const total = await contract.getTotalCommits();
      return Number(total);
    } catch (err) {
      console.error("❌ Erreur getTotalCommits:", err);
      throw err;
    }
  }, [contract]);

  // Écoute les événements EntropyCommitted
  const onEntropyCommitted = useCallback((callback) => {
    if (!contract) return;

    const filter = contract.filters.EntropyCommitted();
    
    contract.on(filter, (value, timestamp, source, event) => {
      callback({
        value: value.toString(),
        timestamp: Number(timestamp),
        source,
        transactionHash: event?.log?.transactionHash || event?.transactionHash || "",
        blockNumber: event?.log?.blockNumber || event?.blockNumber || 0
      });
    });

    // Retourne une fonction de nettoyage
    return () => {
      contract.off(filter);
    };
  }, [contract]);

  return {
    contract,
    address,
    isLoading,
    error,
    getCurrentEntropy,
    getEntropyHistory,
    commitEntropy,
    getTotalCommits,
    onEntropyCommitted,
  };
}
